import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { AGENT_URL } from '@/config/env'
import { mutateJsonWithAuth, requireAuthToken } from '@/lib/api'
import type { CollectedDocument } from './model'

type CancellationResult = { state: string }

export function CancelDocument({ document }: { document: CollectedDocument }) {
  const { getToken } = useAuth()
  const queries = useQueryClient()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [previewed, setPreviewed] = useState(false)
  const action = document.state === 'preparing' ? 'delete_draft' : 'void'
  const label = action === 'delete_draft' ? 'Delete draft' : 'Void document'
  const mutation = useMutation({
    mutationFn: async (confirmed: boolean) =>
      mutateJsonWithAuth<CancellationResult>({
        token: await requireAuthToken(getToken),
        url: `${AGENT_URL}/signing/documents/${encodeURIComponent(document.id)}/cancel`,
        method: 'POST',
        body: { action, reason: reason.trim(), confirmed },
        errorMessage: 'Could not cancel document.',
      }),
    onSuccess: (result) => {
      if (result.state === 'awaiting_confirmation') setPreviewed(true)
    },
  })
  if (!['preparing', 'awaiting_signature'].includes(document.state)) return null
  const close = () => {
    if (mutation.data && mutation.data.state !== 'awaiting_confirmation') {
      void queries.invalidateQueries({ queryKey: ['issued-signing-documents'] })
    }
    setOpen(false)
    setPreviewed(false)
    setReason('')
    mutation.reset()
  }
  const result = mutation.data
  const finished = result && result.state !== 'awaiting_confirmation'
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        {label}
      </Button>
      <Dialog
        open={open}
        onOpenChange={(value) => {
          if (mutation.isPending) return
          if (!value) close()
          else setOpen(true)
        }}>
        <DialogContent
          onKeyDown={(event) => {
            if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
              event.stopPropagation()
            }
          }}>
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
            <DialogDescription>
              {document.formName} for {document.memberName}. This removes
              signing access and cannot be undone in Careforce. The document
              record and activity history will remain.
            </DialogDescription>
          </DialogHeader>
          <Label htmlFor="cancellation-reason">Reason</Label>
          <Textarea
            id="cancellation-reason"
            value={reason}
            maxLength={1000}
            disabled={mutation.isPending || !!finished}
            onChange={(event) => {
              setReason(event.target.value)
              setPreviewed(false)
              mutation.reset()
            }}
          />
          {mutation.error && (
            <p role="alert" className="text-sm text-destructive">
              {mutation.error.message}
            </p>
          )}
          {finished && (
            <p role="status" className="text-sm">
              {result.state === 'cancellation_unconfirmed'
                ? 'The outcome is not yet confirmed. Check document status before trying again.'
                : ['voided', 'deleted'].includes(result.state)
                  ? 'The document is already cancelled.'
                  : 'Request accepted. The document status will update when the provider confirms it.'}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              disabled={mutation.isPending}
              onClick={close}>
              Close
            </Button>
            {!finished && (
              <Button
                variant={previewed ? 'destructive' : 'default'}
                disabled={!reason.trim() || mutation.isPending}
                onClick={() => mutation.mutate(previewed)}>
                {mutation.isPending
                  ? previewed
                    ? 'Submitting…'
                    : 'Checking…'
                  : previewed
                    ? `Confirm ${action === 'void' ? 'void' : 'draft deletion'}`
                    : 'Continue'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
